import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { Store } from '../../../services/store';
import { ReportExportsStore } from '../../../services/report-exports.store';
import { toVoid } from '../../../utils/void';
import { downloadBlob } from '../../../utils/download';
import { ReportRange } from '../../../utils/report-range';
import { ReportExportFormat } from '../reports/reports.store';
import { MaterialRequirementsProvider } from './material-requirements.provider';
import { MaterialRequirementsDto } from './models/material-requirements.dto';
import { buildMaterialRequirementsCsv, materialRequirementsCsvFileName } from './models/material-requirements-csv';

export interface MaterialRequirementsState extends ReportRange {
  loading: boolean;
  requirements: MaterialRequirementsDto | undefined;
  /** Page state, not URL state, as on the progress log: back to Excel on the next visit. */
  format: ReportExportFormat;
}

// The real range comes from the URL on the first load (the page decides the defaults)
const INITIAL_STATE: MaterialRequirementsState = {
  from: '',
  to: '',
  loading: false,
  requirements: undefined,
  format: 'xlsx'
};

// Zapotrzebowanie materiałowe (0118), the progress log's store over another readout: the URL owns
// the range, the Excel file runs in the shell's tray, the CSV is built here from the rows on screen
@Injectable({ providedIn: 'root' })
export class MaterialRequirementsStore extends Store<MaterialRequirementsState> {
  constructor(
    private provider: MaterialRequirementsProvider,
    private exports: ReportExportsStore
  ) {
    super(INITIAL_STATE);
  }

  // Only the first load shows Loading…, later ones keep the rows until the answer. The days
  // go as they are: planned windows are calendar days, so no local-midnight instants here
  load(range: ReportRange): Observable<void> {
    this.update(state => ({ ...state, ...range, loading: true }));

    return this.provider.getMaterialRequirements(range.from, range.to)
      .pipe(
        tap(requirements => {
          this.update(state => ({ ...state, loading: false, requirements }));
        }),
        toVoid()
      );
  }

  setFormat(format: ReportExportFormat): void {
    this.update(state => ({ ...state, format }));
  }

  export(): void {
    const state = this.state;
    const range = { from: state.from, to: state.to };

    if (state.format === 'csv') {
      const exportedAt = new Date();
      const csv = buildMaterialRequirementsCsv(state.requirements?.rows ?? [], range, exportedAt);

      downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), materialRequirementsCsvFileName(range, exportedAt));

      return;
    }

    this.exports.start('materials', range);
  }
}
